import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import type { Theme } from '@emotion/react'
import { css } from '@emotion/react'

import * as CopyText from '@segment/matcha/recipes/CopyText'

import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'

import EmailCaptureForm from '@segment/templates/Forms/EmailCaptureForm'

import heroPng2x from './assets/Hero@2x.png'
import heroWebp from './assets/Hero.webp'
import heroWebp2x from './assets/Hero@2x.webp'

const copy = {
  heading: `The State of Personalization 2022`,
  description: `Brought to you by Twilio Segment`,
}

const button = {
  text: `Download the report`,
  url: `https://segment.com/pdfs/State-of-Personalization-Report-Twilio-Segment-2022.pdf`,
}

const TRACKING_EVENT = `Content Downloaded`

const Hero: FC<PropsWithChildren<unknown>> = () => {
  return (
    <Wrapper>
      <StyledHeroBase variant="2Col">
        <HeroCopy>
          <CopyText.Heading variant="h1" tag="h1">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description variant="pExtraLarge" tag="p">
            {copy.description}
          </CopyText.Description>
          <StyledHeroRecipeForm>
            <EmailCaptureForm
              resourceType="report"
              buttonText={button.text}
              successButtonText={button.text}
              additionalTrackEvent={TRACKING_EVENT}
              buttonUrl={button.url}
              successMessageAppearance="twilioLight"
              customFormIdentifier="state-of-personalization-report"
              enableJobTitleField={true}
            />
          </StyledHeroRecipeForm>
        </HeroCopy>

        <HeroContents>
          <picture>
            <source type="image/webp" srcSet={`${heroWebp}, ${heroWebp2x} 2x`} />
            <HeroRecipe.Image
              width="640"
              height="476"
              src={heroPng2x}
              alt="The State of Personalization"
              loading="lazy"
            />
          </picture>
        </HeroContents>
      </StyledHeroBase>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  background-color: ${({ theme }) => theme.palette.twilio.green[10]};
`

const StyledHeroBase = styled(HeroBase)`
  ${HeroRecipe.Wrapper} {
    justify-content: unset;
  }
`

const heroColumnStyles = (theme: Theme) => css`
  max-width: 568px;
  ${theme.breakpoints.up(`lg`)} {
    max-width: unset;
  }
`

const HeroCopy = styled(HeroRecipe.Copy)`
  ${({ theme }) => heroColumnStyles(theme)};
  text-align: left;
`

const HeroContents = styled(HeroRecipe.Contents)`
  ${({ theme }) => heroColumnStyles(theme)};
  justify-items: unset;
`

const StyledHeroRecipeForm = styled(HeroRecipe.Form)`
  max-width: 100%;

  ${({ theme }) => theme.breakpoints.between(`sm`, `md`)} {
    max-width: 488px;
  }
`

export default Hero
